// Nomad-mon Go! — v11 FORGE SOUNDS, synthesised in WebAudio (no new assets).
// buildForgeSfx(ctx, dest, t0, { kind, tier }) schedules the whole cue and
// returns its end time. Pure graph construction: it runs identically on an
// OfflineAudioContext, which is how scripts/forge-sfx-check.mjs measures the
// rendered TRUE PEAK (must stay <= -1 dBTP; the cue ends in its own limiter
// and a fixed output trim so the ceiling holds before the game's master bus).
//
// FUSION (~5.6s): a filtered-saw RISER with a noise whoosh while the reagents
// fly in and burn, a sub-drop IMPACT on the forge flash (t0 + 2.2s), a bright
// shock "zing", then a shimmering add9 chord as the hologram rises. Higher
// tiers lift the chord root and thicken the riser.
// DUO (~3.2s): two crossing swooshes, a "fuse" zing as the cards
// merge (t0 + 1.0s), a lighter impact and a two-note chime.
//
// v197 CRACKLE-BOOM + CHOIR (the god-pull cues, reused): on top of all that,
// the reveal lands the level-up BOOM-CRACKLE (levelup-sfx.js: phone-audible
// punch in 60-600 Hz + a 2-8 kHz crackle field + slam/deep/deep-echo body +
// god debris) with the god-pull choir (god-aaah, choir crown) swelling INTO the
// hit and blooming after it, and the Fusion's AFTERSHOCK (hit + 0.5 s, the
// second visual shake) gets a smaller second boom. The Duo gets the same,
// scaled down. Every layer — the synth stack included — rides the level-up
// mixing bus (52 Hz high-pass, glue compressor, oversampled ceiling), so the
// stack holds <= -1 dBTP. Sampled layers come from `buf(name)` (audio.sfxBuffer
// in the game); a buffer that is not decoded yet simply drops its layer.
import { master, sample, boom, crackle, crackleSchedule } from './levelup-sfx.js';

export const FORGE_IMPACT_AT = { fusion: 2.2, duo: 1.0 };
export const FORGE_AFTERSHOCK = 0.5;   // forge-ceremony.js: the Fusion's second shake, after the hit
/** The god-pull SFX the forge cues mix in (audio.loadSfx before the ceremony). */
export const FORGE_SFX = ['godaaah', 'choir', 'godcrown', 'slam', 'deep', 'deepecho', 'goddebris'];

function noiseBuffer(ctx, secs) {
  const n = Math.max(1, Math.floor(ctx.sampleRate * secs));
  const b = ctx.createBuffer(1, n, ctx.sampleRate);
  const d = b.getChannelData(0);
  let s = 1234567;
  for (let i = 0; i < n; i++) { s = (s * 1103515245 + 12345) >>> 0; d[i] = (s / 4294967296) * 2 - 1; }
  return b;
}
function env(g, t, a, peak, hold, rel) {
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + a);
  g.gain.setValueAtTime(peak, t + a + hold);
  g.gain.exponentialRampToValueAtTime(0.0001, t + a + hold + rel);
}

export function buildForgeSfx(ctx, dest, t0, { kind = 'fusion', tier = 1, buf = () => null, seed = 11 } = {}) {
  // the level-up mixing bus: the whole cue (synth + samples) ends in its ceiling
  const M = master(ctx, dest, kind === 'fusion' ? 0.55 : 0.5);
  // the synth stack keeps its own limiter + trim in front of that bus
  const lim = ctx.createDynamicsCompressor();
  lim.threshold.value = -9; lim.knee.value = 0; lim.ratio.value = 20;
  lim.attack.value = 0.001; lim.release.value = 0.12;
  const trim = ctx.createGain(); trim.gain.value = 0.8;
  const bus = ctx.createGain(); bus.gain.value = 1;
  bus.connect(lim); lim.connect(trim); trim.connect(M);
  const hit = t0 + FORGE_IMPACT_AT[kind];
  const lift = kind === 'fusion' ? Math.min(1, (tier - 1) / 23) : 0;
  // a 45 ms hole before the hit (the level-up recipe): the synth stack dips so
  // the boom's transient lands in air instead of on top of the riser
  bus.gain.setValueAtTime(1, hit - 0.05);
  bus.gain.linearRampToValueAtTime(0.3, hit - 0.012);
  bus.gain.linearRampToValueAtTime(1, hit + 0.004);
  const godEnd = godLayers(ctx, M, hit, { kind, lift, buf, seed });

  if (kind === 'fusion') {
    // RISER: two detuned saws through a sweeping low-pass
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.Q.value = 6;
    lp.frequency.setValueAtTime(180, t0); lp.frequency.exponentialRampToValueAtTime(5200 + 2000 * lift, hit);
    const rg = ctx.createGain(); env(rg, t0, 2.0, 0.22 + 0.08 * lift, 0.15, 0.25);
    for (const det of [-9, 7, ...(lift > 0.5 ? [19] : [])]) {
      const o = ctx.createOscillator(); o.type = 'sawtooth';
      o.frequency.setValueAtTime(55, t0); o.frequency.exponentialRampToValueAtTime(220, hit);
      o.detune.value = det; o.connect(lp); o.start(t0); o.stop(hit + 0.5);
    }
    lp.connect(rg); rg.connect(bus);
  } else {
    // two crossing swooshes (the cards sliding in from both sides)
    for (const [dt, from, to] of [[0, 400, 2600], [0.12, 2800, 500]]) {
      const n = ctx.createBufferSource(); n.buffer = noiseBuffer(ctx, 1.1);
      const bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.Q.value = 2.2;
      bp.frequency.setValueAtTime(from, t0 + dt); bp.frequency.exponentialRampToValueAtTime(to, hit);
      const g = ctx.createGain(); env(g, t0 + dt, 0.55, 0.32, 0.2, 0.25);
      n.connect(bp); bp.connect(g); g.connect(bus); n.start(t0 + dt); n.stop(hit + 0.4);
    }
  }
  // whoosh into the hit
  {
    const n = ctx.createBufferSource(); n.buffer = noiseBuffer(ctx, 3);
    const bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.Q.value = 1.4;
    const w0 = Math.max(t0, hit - 1.2);
    bp.frequency.setValueAtTime(300, w0); bp.frequency.exponentialRampToValueAtTime(6000, hit);
    const g = ctx.createGain(); env(g, w0, Math.max(0.05, hit - w0 - 0.05), kind === 'fusion' ? 0.3 : 0.18, 0.02, 0.35);
    n.connect(bp); bp.connect(g); g.connect(bus); n.start(w0); n.stop(hit + 0.5);
  }
  // IMPACT: sub drop + body thump + noise crack
  {
    const o = ctx.createOscillator(); o.type = 'sine';
    o.frequency.setValueAtTime(kind === 'fusion' ? 130 : 160, hit); o.frequency.exponentialRampToValueAtTime(38, hit + 0.6);
    const g = ctx.createGain(); env(g, hit, 0.004, kind === 'fusion' ? 0.95 : 0.6, 0.05, 0.9);
    o.connect(g); g.connect(bus); o.start(hit); o.stop(hit + 1.1);
    const n = ctx.createBufferSource(); n.buffer = noiseBuffer(ctx, 0.6);
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 2400;
    const ng = ctx.createGain(); env(ng, hit, 0.002, 0.5, 0.01, 0.35);
    n.connect(lp); lp.connect(ng); ng.connect(bus); n.start(hit); n.stop(hit + 0.6);
  }
  // shock "zing" (fusion) / fuse zing (duo)
  {
    const o = ctx.createOscillator(); o.type = 'triangle';
    o.frequency.setValueAtTime(kind === 'fusion' ? 1800 : 2600, hit); o.frequency.exponentialRampToValueAtTime(kind === 'fusion' ? 5200 : 7000, hit + 0.35);
    const g = ctx.createGain(); env(g, hit + 0.01, 0.01, 0.12, 0.05, 0.5);
    o.connect(g); g.connect(bus); o.start(hit); o.stop(hit + 0.7);
  }
  // shimmer chord as the card rises (add9; root climbs with tier)
  const root = (kind === 'fusion' ? 220 : 330) * Math.pow(2, (lift * 5) / 12);
  const ratios = kind === 'fusion' ? [1, 1.25, 1.5, 2.25, 3] : [1, 1.5, 2];
  const chordAt = hit + (kind === 'fusion' ? 0.25 : 0.12);
  const chordLen = kind === 'fusion' ? 3.0 : 1.9;
  ratios.forEach((r, i) => {
    const o = ctx.createOscillator(); o.type = i % 2 ? 'sine' : 'triangle';
    o.frequency.value = root * r;
    const vib = ctx.createOscillator(); vib.frequency.value = 5 + i * 0.7;
    const vg = ctx.createGain(); vg.gain.value = root * r * 0.004;
    vib.connect(vg); vg.connect(o.frequency);
    const g = ctx.createGain(); env(g, chordAt + i * 0.06, 0.08, 0.1 / Math.sqrt(i + 1), 0.6, chordLen);
    o.connect(g); g.connect(bus);
    o.start(chordAt); vib.start(chordAt);
    o.stop(chordAt + chordLen + 1); vib.stop(chordAt + chordLen + 1);
  });
  return Math.max(chordAt + chordLen + 0.9, godEnd);
}

// The CRACKLE-BOOM + CHOIR on the reveal (the god-pull / level-up cues).
// Returns when the last layer has died away.
function godLayers(ctx, M, hit, { kind, lift, buf, seed }) {
  if (kind === 'fusion') {
    // CHOIR swelling into the hit (one aah rising + the crown entering late)
    sample(ctx, M, buf('godaaah'), hit - 1.8, { gain: 0.72, attack: 1.75, dur: 1.95, release: 0.25 });
    sample(ctx, M, buf('godcrown'), hit - 1.0, { gain: 0.32, attack: 0.95, dur: 1.15, release: 0.2 });
    // BOOM: phone-audible synth punch + slam / deep body + deep-echo tail
    boom(ctx, M, hit, { size: 1.3 + 0.2 * lift, seed });
    sample(ctx, M, buf('slam'), hit, { gain: 0.95, dur: 1.6, release: 0.8 });
    sample(ctx, M, buf('slam'), hit + 0.02, { gain: 0.6, rate: 0.6, dur: 1.8, release: 0.9 });
    sample(ctx, M, buf('deep'), hit, { gain: 1.1, rate: 0.75, dur: 1.6, release: 0.8 });
    sample(ctx, M, buf('deepecho'), hit + 0.02, { gain: 0.9, dur: 2.4, release: 1.1 });
    // CRACKLE: the 2-8 kHz impulse field + god-pull glass debris
    crackle(ctx, M, hit, crackleSchedule(seed, { count: 80 + Math.round(30 * lift), span: 1.3 }), 2.1);
    sample(ctx, M, buf('goddebris'), hit + 0.05, { gain: 0.35, rate: 1.1, dur: 2.4, release: 1.1 });
    // CHOIR BLOOM after the hit: the aah lower and wide, the choir, the crown
    sample(ctx, M, buf('godaaah'), hit + 0.03, { gain: 0.5 + 0.1 * lift, rate: 0.84, dur: 3.4, release: 1.8, pan: -0.25 });
    sample(ctx, M, buf('choir'), hit + 0.08, { gain: 0.42, dur: 3.2, release: 1.6, pan: 0.25 });
    sample(ctx, M, buf('godcrown'), hit + 0.1, { gain: 0.22 + 0.08 * lift, dur: 3.4, release: 1.8 });
    // AFTERSHOCK (the second shake): a smaller boom + a low slam
    const as = hit + FORGE_AFTERSHOCK;
    boom(ctx, M, as, { size: 0.6, seed: seed + 5 });
    sample(ctx, M, buf('slam'), as, { gain: 0.5, rate: 0.62, dur: 1.4, release: 0.7 });
    crackle(ctx, M, as, crackleSchedule(seed + 9, { count: 30, span: 0.7 }), 1.3);
    return hit + 3.6;
  }
  // DUO: the same recipe, scaled down
  sample(ctx, M, buf('godaaah'), hit - 0.9, { gain: 0.48, rate: 1.06, attack: 0.85, dur: 1.0, release: 0.15 });
  boom(ctx, M, hit, { size: 0.75, seed });
  sample(ctx, M, buf('slam'), hit, { gain: 0.7, dur: 1.3, release: 0.6 });
  sample(ctx, M, buf('deep'), hit, { gain: 0.7, rate: 0.85, dur: 1.3, release: 0.6 });
  crackle(ctx, M, hit, crackleSchedule(seed, { count: 45, span: 0.85 }), 1.6);
  sample(ctx, M, buf('goddebris'), hit + 0.05, { gain: 0.2, rate: 1.2, dur: 1.6, release: 0.8 });
  sample(ctx, M, buf('choir'), hit + 0.06, { gain: 0.4, rate: 1.06, dur: 2.7, release: 1.3 });
  return hit + 2.9;
}

// ---------------------------------------------------------------- v324 FORGE EPIC (was v277 THE ANVIL)
// The forge moment (forge-ceremony.js). Jack: "the forge card animation is not nearly cool enough ... massively more epic".
// BUILD-UP: the anvil slams down, the ingredient cards are pulled in a spiral into a molten CORE above it (the count ticks up),
// the screen closes in, a rumble swells; the core drops onto the anvil as the melt. STRIKES (wait for the server: the GATE):
// 2 (Duo) or 3 (Fusion) escalating blows, the last one a white-out impact + shockwave. REVEAL: the card spins out of the light
// to face the player, its frame + holo ignite edge to edge, its name slams in. Everything scales with the RANK (0 Duo, 1-3 the
// Fusion bands ember / plasma / prismatic): a higher rank holds the white longer, spins further, hits harder, sings bigger.
// Two cues, because the strikes wait for the server: INTRO from the tap (furnace, the anvil landing, the vortex pull, the rumble
// build, the melt) and STRIKES from the gate (every blow + the reveal). Recorded takes only (ElevenLabs: scripts/gen-sfx-forgecer.mjs,
// gen-sfx-nameforge.mjs, gen-sfx-forge-epic.mjs) — a buffer not decoded yet simply drops its layer, nothing is synthesised.
// Both ride the level-up mixing bus (<= -1 dBTP each; scripts/forge-sfx-check.mjs). All times are seconds.
export const ANVIL = {
  // v330 Jack: "slow down the animation a little bit": the builds + holds run ~1.28x (was land .55, pull .7/.6/.8,
  // sink 2.05, melt 2.25, gate 2.45, strikes duo .38/1.13, fusion .38/.95/1.7); the impacts (swing .13, hit-stops, burst) stay crisp
  drop: 0.08, land: 0.7,                  // the anvil falls, lands
  pull: 0.9, pullDur: 0.78, pullSpan: 1.0, // card i leaves at pull + i * min(.09, pullSpan / n), reaches the core pullDur later
  sink: 2.62, melt: 2.88, gate: 3.14,     // the core sinks onto the anvil; the melt; strikes may start (server permitting)
  strikes: { duo: [0.48, 1.45], fusion: [0.48, 1.22, 2.18] },  // blows, relative to the gate (windup .3 / final .5, swing .13, rebound .12)
  stop: [0.07, 0.1, 0.22],                // hit-stop per blow (the last entry = the final blow; + 0.02 per rank)
  burst: 0.1,                             // the light bursts out this long after the final blow
};
export const ANVIL_SFX = ['fcroar', 'fcdrop', 'fcmelt', 'fcquench', 'nfhit1', 'nfhit2', 'nfhit3', 'nfimpact', 'nfriser',
  'slotwhoosh', 'deep', 'slam', 'godaaah', 'choir', 'godcrown', 'goddebris',
  'ferumble', 'fevortex', 'feimpact', 'feblast', 'feslam', 'ferevealbig', 'ferevealsmall'];
export const anvilPullAt = (i, n) => ANVIL.pull + i * Math.min(0.09, ANVIL.pullSpan / Math.max(1, n));
/** rank: 0 Duo, 1..3 = Fusion band + 1 (ember / plasma / prismatic). */
export const forgeRank = (kind, band) => (kind === 'duo' ? 0 : 1 + band);
/** The reveal's beats, seconds after the burst (B = final blow + ANVIL.burst): the card emerges, spins to face the player
 *  (the frame ignites as it arrives), settles, the name slams, the player may continue. */
export function revealTimes(rank) {
  // v330 x1.28 on the spin, the holds and the settle (was faced .85 + .12 rank, settle .15 + .1 rank, name +.5, meta +.25, ready +.55)
  const emerge = 0.28, faced = emerge + 1.09 + 0.15 * rank;
  const settle = faced + 0.2 + 0.13 * rank;
  const name = settle + 0.64;
  return { emerge, faced, ignite: faced - 0.22, settle, settleDur: 0.7, name, meta: name + 0.32, ready: name + 0.7,
    white: 0.1 + 0.05 * rank };      // the white-out holds this long after the final blow
}

/** INTRO cue: from the tap to (a little past) the gate. n = cards pulled in. */
export function buildAnvilIntroSfx(ctx, dest, t0, { kind = 'fusion', rank = 1, n = 4, buf = () => null } = {}) {
  const M = master(ctx, dest, 0.5);
  const A = ANVIL;
  const end = t0 + A.gate + 1.9;
  // the furnace: a roaring bed from the dark, held through the gate (the strikes cue takes over)
  sample(ctx, M, buf('fcroar'), t0, { gain: 0.55, attack: 0.5, dur: end - t0, release: 1.1 });
  // the anvil slams down: a heavy air whoosh, the floor thud, a low body
  sample(ctx, M, buf('slotwhoosh'), t0 + A.land - 0.4, { gain: 0.42, rate: 0.7 });
  sample(ctx, M, buf('fcdrop'), t0 + A.land - 0.01, { gain: 1.0 });
  sample(ctx, M, buf('deep'), t0 + A.land, { gain: 0.5, rate: 0.8, dur: 1.3, release: 0.7 });
  // THE PULL: the vortex sucks the cards into the core (a deeper second layer from plasma up)
  sample(ctx, M, buf('fevortex'), t0 + A.pull - 0.1, { gain: 0.85, dur: 2.3, release: 0.6 });
  if (rank >= 2) sample(ctx, M, buf('fevortex'), t0 + A.pull + 0.25, { gain: 0.45, rate: 0.82, dur: 2.0, release: 0.6, pan: 0.2 });
  const k = Math.min(n, 3);          // a few card whooshes inside the vortex, panned across it
  for (let j = 0; j < k; j++) {
    const i = k > 1 ? Math.round((j / (k - 1)) * (n - 1)) : 0;
    sample(ctx, M, buf('slotwhoosh'), t0 + anvilPullAt(i, n), { gain: 0.16, rate: 1.2 + 0.1 * j, pan: k > 1 ? (j / (k - 1)) * 0.8 - 0.4 : 0 });
  }
  // THE RUMBLE BUILD: the take climbs to its peak at its last frame; that frame lands on the gate
  const rb = buf('ferumble');
  if (rb) { const off = Math.max(0, rb.duration - 0.05 - A.gate); sample(ctx, M, rb, t0 + Math.max(0, A.gate + 0.05 - rb.duration), { gain: 0.75 + 0.08 * rank, offset: off, dur: rb.duration - off, release: 0.25 }); }
  // the core sinks into the anvil: molten metal bubbling and hissing
  sample(ctx, M, buf('fcmelt'), t0 + A.sink, { gain: 0.75, attack: 0.12, dur: end - (t0 + A.sink), release: 0.8 });
  return end;
}

/** STRIKES cue: t0 = the gate. `from` = the first blow to play (a skip jumps straight to the final one).
 *  Escalates blow by blow; the final blow is the colossal impact + the molten blast; the reveal sings by rank. */
export function buildAnvilStrikeSfx(ctx, dest, t0, { kind = 'fusion', rank = 1, buf = () => null, from = 0 } = {}) {
  const M = master(ctx, dest, 0.55);
  const S = ANVIL.strikes[kind], last = S.length - 1;
  const now = ctx.currentTime + 0.005;
  const HITS = ['nfhit1', 'nfhit3', 'nfhit2'];
  // the riser: molten roar climbing into the first blow still to play
  const r1 = t0 + S[from], rs = Math.max(r1 - 1.0, now);
  if (r1 - rs > 0.2) sample(ctx, M, buf('nfriser'), rs, { gain: 0.5, offset: 1.0 - (r1 - rs), dur: r1 - rs + 0.05, release: 0.06 });
  for (let i = from; i <= last; i++) {
    const hit = t0 + S[i], fin = i === last, k = (i + 1) / S.length;
    const sw = Math.max(hit - 0.2, now);             // the hammer's down-swing
    if (hit - sw > 0.08) sample(ctx, M, buf('slotwhoosh'), sw, { gain: 0.3 + 0.15 * k, rate: 1.35, dur: hit - sw + 0.06, release: 0.05 });
    if (!fin) {
      // THE BLOW: a cinematic hammer-on-anvil take, heavier each time (lower + louder)
      sample(ctx, M, buf(HITS[i % HITS.length]), hit, { gain: 0.72 + 0.22 * k, rate: 1.02 - 0.06 * i, pan: i % 2 ? 0.12 : -0.12 });
      sample(ctx, M, buf('deep'), hit, { gain: 0.3 + 0.2 * k, rate: 0.9, dur: 1.0, release: 0.5 });
    } else {
      // THE FINAL BLOW: the colossal hammer-on-anvil boom, the trailer impact under it, the molten shockwave blast, debris
      sample(ctx, M, buf('feimpact'), hit, { gain: 0.9 + 0.03 * rank });
      sample(ctx, M, buf('nfimpact'), hit, { gain: 0.6 + 0.08 * rank });
      sample(ctx, M, buf('slam'), hit + 0.01, { gain: 0.45, rate: 0.62, dur: 1.6, release: 0.8 });
      sample(ctx, M, buf('feblast'), hit + 0.03, { gain: 0.7 + 0.08 * rank });
      sample(ctx, M, buf('goddebris'), hit + 0.1, { gain: 0.18 + 0.05 * rank, rate: 1.05, dur: 2.0, release: 1.0 });
    }
  }
  // the choir is drawn in by the final swing; then the reveal by rank: the stings, the quench as the card cools,
  // the shimmer as its frame ignites, the slam under its name, the choir bloom for plasma + prismatic
  const F = t0 + S[last], rv = F + ANVIL.burst, R = revealTimes(rank);
  const sw = Math.min(1.1, Math.max(0.35, S[last] - (from > 0 ? S[from] - 0.3 : 0) - 0.15));
  const sw0 = Math.max(F - sw, now), swl = F - sw0;
  if (swl > 0.15) sample(ctx, M, buf('godaaah'), sw0, { gain: 0.24 + 0.06 * rank, attack: swl * 0.95, dur: swl + 0.05, release: 0.1 });
  const em = rv + R.emerge;
  if (rank === 0) sample(ctx, M, buf('ferevealsmall'), em, { gain: 0.9 });
  else {
    sample(ctx, M, buf('ferevealbig'), em, { gain: 0.6 + 0.12 * rank });
    if (rank === 1) sample(ctx, M, buf('ferevealsmall'), em + 0.05, { gain: 0.45 });
  }
  sample(ctx, M, buf('fcquench'), em + 0.2, { gain: 0.32, attack: 0.08, dur: 1.6, release: 0.8 });
  sample(ctx, M, buf('ferevealsmall'), rv + R.ignite, { gain: 0.25 + 0.05 * rank, rate: 1.25 });
  sample(ctx, M, buf('feslam'), rv + R.name, { gain: 0.85 + 0.05 * rank });
  sample(ctx, M, buf('deep'), rv + R.name, { gain: 0.3, rate: 1.1, dur: 0.9, release: 0.5 });
  if (rank >= 2) {
    sample(ctx, M, buf('choir'), em + 0.15, { gain: 0.22 + 0.08 * rank, attack: 0.3, dur: 3.6, release: 1.8, pan: 0.2 });
    sample(ctx, M, buf('godaaah'), em + 0.1, { gain: 0.16 + 0.07 * rank, rate: 0.84, attack: 0.3, dur: 3.8, release: 1.9, pan: -0.2 });
  }
  if (rank >= 3) sample(ctx, M, buf('godcrown'), rv + R.ignite, { gain: 0.32, dur: 3.4, release: 1.7 });
  return rv + R.ready + 2.4;
}

// a filtered-noise gesture from f0 to f1 (whoosh / swell / swipe)
function sweep(ctx, bus, at, dur, f0, f1, peak, q = 1.2, attack = 0.6) {
  const n = ctx.createBufferSource(); n.buffer = noiseBuffer(ctx, dur + 0.1);
  const bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.Q.value = q;
  bp.frequency.setValueAtTime(f0, at); bp.frequency.exponentialRampToValueAtTime(f1, at + dur);
  const g = ctx.createGain(); env(g, at, Math.max(0.005, dur * attack), peak, 0.005, Math.max(0.03, dur * (1 - attack)));
  n.connect(bp); bp.connect(g); g.connect(bus); n.start(at); n.stop(at + dur + 0.1);
}

/** HOLD TO FORGE (forgeui.js): a rising, heating tone for `dur` s, then a held
 *  shimmer; the sheet ramps the cue's output to 0 when the press ends. */
export function buildChargeSfx(ctx, dest, t0, { dur = 0.85 } = {}) {
  const M = master(ctx, dest, 0.45);
  const d = Math.max(0.12, dur);
  const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.Q.value = 7;
  lp.frequency.setValueAtTime(300, t0); lp.frequency.exponentialRampToValueAtTime(4200, t0 + d);
  const g = ctx.createGain(); env(g, t0, d * 0.9, 0.16, 0.25, 0.2);
  for (const det of [-8, 9]) {
    const o = ctx.createOscillator(); o.type = 'sawtooth';
    o.frequency.setValueAtTime(82, t0); o.frequency.exponentialRampToValueAtTime(330, t0 + d);
    o.detune.value = det; o.connect(lp); o.start(t0); o.stop(t0 + d + 0.5);
  }
  lp.connect(g); g.connect(M);
  sweep(ctx, M, t0, d, 1200, 6500, 0.06, 0.8, 0.9);
  crackle(ctx, M, t0 + d * 0.3, crackleSchedule(77, { count: 24, span: d * 0.7, delay: 0 }), 0.22);
  return t0 + d + 0.5;
}
